import { useEffect } from 'react';
import { useAuth } from '@/hooks/app/useAuth';
import { parseSearchParams, setGlobalUnauthorizedHandler } from '@/lib/utils';
import socketService from '@/lib/socket';
import { useUIStore } from '@/stores';
import { fetchUserProfile } from '@/services/userService';

// 从服务端 profile 恢复语言配置
const applyServerLanguage = async () => {
  try {
    const profile = await fetchUserProfile();
    if (profile?.settings?.language) {
      const languageMap: { [key: string]: string } = {
        'en-us': 'en',
        'zh-cn': 'zh',
      };
      const mappedCode =
        languageMap[profile.settings.language] || profile.settings.language;
      const { default: i18nInstance } = await import('@/i18n/config');
      i18nInstance.changeLanguage(mappedCode);
    }
  } catch {
    // 获取失败不影响页面加载，保持默认英文
  }
};

interface UseAppInitializationOptions {
  shareConversationId: string | null;
  setShareConversationId: (id: string) => void;
  loadProjects: () => Promise<void>;
  loadConversations: (
    shareConversation?: string | null,
    force?: boolean
  ) => Promise<void>;
  refreshConversations: () => Promise<void>;
}

// Re-export for use in App.tsx

export function useAppInitialization({
  shareConversationId,
  setShareConversationId,
  loadProjects,
  loadConversations,
  refreshConversations,
}: UseAppInitializationOptions) {
  const { setCurrentView } = useUIStore();
  const {
    initializeAuth,
    handleLoginSuccess: onLoginSuccess,
    handleUnauthorized,
  } = useAuth();

  useEffect(() => {
    const initializeApp = async () => {
      const urlParams = new URLSearchParams(location.search);
      const isOAuthRedirect = urlParams.has('code');

      if (location.search) {
        const params = parseSearchParams(location.search);
        if (params['conversationId']) {
          setShareConversationId(params['conversationId']);
        }
      }

      setGlobalUnauthorizedHandler((redirectUrl?: string) => {
        handleUnauthorized(redirectUrl);
        setCurrentView('login');
      });

      if (isOAuthRedirect) {
        setCurrentView('login');
        return;
      }

      const { isLoggedIn: loggedIn } = await initializeAuth();

      if (!loggedIn) {
        setCurrentView('login');
        return;
      }

      if (
        localStorage.getItem('user-token') &&
        localStorage.getItem('isLoggedIn') === 'true'
      ) {
        socketService.on('UPLOAD_FINISH', () => {
          if (window.refreshMentionDropdown) {
            window.refreshMentionDropdown();
          }
          if (window.refreshPDFList) {
            window.refreshPDFList();
          }
        });
      }

      // 已登录用户刷新页面：从服务端恢复语言配置
      await applyServerLanguage();

      if (!shareConversationId) {
        await loadProjects();
        await loadConversations();
      }
    };
    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoginSuccess = async (_token: string) => {
    // 注意：Login.tsx 已经直接设置了 currentView 和 URL hash
    // 这里只需要调用 onLoginSuccess 进行后续初始化（获取用户信息等）
    await onLoginSuccess();

    // 新登录：从服务端恢复语言配置（如用户曾保存过中文，立即切换）
    await applyServerLanguage();

    socketService.on('UPLOAD_FINISH', () => {
      if (window.refreshMentionDropdown) {
        window.refreshMentionDropdown();
      }
      if (window.refreshPDFList) {
        window.refreshPDFList();
      }
    });

    try {
      await Promise.all([loadProjects(), refreshConversations()]);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  return {
    handleLoginSuccess,
  };
}
